import type { Dispatch, SetStateAction } from "react";
import type { StoredProject } from "../model/storage.ts";
import type { SwmmProject } from "../model/inp/types.ts";
import type { ElementRef } from "../model/inp/properties.ts";
import type { Theme } from "../model/theme.ts";
import type { Language } from "../i18n/language.ts";
import type { Translate } from "../i18n/hooks.ts";
import type { useDialogToggles } from "./useDialogToggles.ts";
import type { useSimulationWorker } from "./useSimulationWorker.ts";
import type { useResultsDashboard } from "./useResultsDashboard.ts";
import { ProjectMenu } from "./ProjectMenu.tsx";
import { CloseProjectIcon } from "./CloseProjectIcon.tsx";
import { BackgroundImageIcon } from "./BackgroundImageIcon.tsx";
import { GeoreferenceIcon } from "./GeoreferenceIcon.tsx";
import { GlobeIcon } from "./GlobeIcon.tsx";
import { LayoutGridIcon } from "./LayoutGridIcon.tsx";
import { MapSettingsMenu } from "./MapSettingsMenu.tsx";
import { ListIcon } from "./ListIcon.tsx";
import { ElementSearch } from "./ElementSearch.tsx";
import { UndoIcon } from "./UndoIcon.tsx";
import { RedoIcon } from "./RedoIcon.tsx";
import { OptionsIcon } from "./OptionsIcon.tsx";
import { PlayIcon } from "./PlayIcon.tsx";
import { StopIcon } from "./StopIcon.tsx";
import { ResultsDashboardIcon } from "./ResultsDashboardIcon.tsx";
import { SummaryIcon } from "./SummaryIcon.tsx";
import { StatisticsIcon } from "./StatisticsIcon.tsx";
import { ResultsAnimationIcon } from "./ResultsAnimationIcon.tsx";
import { SettingsMenu } from "./SettingsMenu.tsx";
import { Logo } from "./Logo.tsx";
import { IconButton } from "./IconButton.tsx";
import { TOOLBAR_MENU_GROUP } from "./toolbarMenuGroup.ts";

/**
 * The workspace's own `<header>` — split out of App.tsx (P1 deep-review: 1,342
 * lines / 105-file fan-out, no god-module), and later split again (UI
 * redesign phase 3) out of the single AppToolbar.tsx (491 lines, 47 props)
 * into this — app identity, the current project's own actions, map mode,
 * undo/redo, running a simulation and its results — and ToolRail.tsx, the
 * vertical strip of map-EDITING tools (select/draw/measure/query/thematic)
 * docked to the left of the map instead. Purely a rendering shell: every
 * piece of state/behavior it needs is a prop, all of it still owned and
 * derived by App.tsx's own hooks.
 */
export function AppBar({
  loaded,
  project,
  t,
  dialogs,
  onRenameProject,
  onDuplicateProject,
  onExportInp,
  onDeleteProject,
  onCloseProject,
  mapMode,
  setMapMode,
  showOverview,
  setShowOverview,
  showLinkDirection,
  setShowLinkDirection,
  sidebarVisible,
  setSidebarVisible,
  onSelectFromList,
  canUndo,
  onUndo,
  canRedo,
  onRedo,
  onRunOrCancelSimulation,
  simulationWorker,
  resultsDashboard,
  resultsAnimationOpen,
  onToggleResultsAnimation,
  theme,
  onToggleTheme,
  language,
  onChangeLanguage,
}: {
  loaded: StoredProject;
  project: SwmmProject;
  t: Translate;
  dialogs: ReturnType<typeof useDialogToggles>;
  onRenameProject: (name: string) => void;
  onDuplicateProject: (name: string) => void;
  onExportInp: () => void;
  onDeleteProject: () => void;
  onCloseProject: () => void;
  mapMode: "plane" | "geo";
  setMapMode: Dispatch<SetStateAction<"plane" | "geo">>;
  showOverview: boolean;
  setShowOverview: Dispatch<SetStateAction<boolean>>;
  showLinkDirection: boolean;
  setShowLinkDirection: Dispatch<SetStateAction<boolean>>;
  sidebarVisible: boolean;
  setSidebarVisible: Dispatch<SetStateAction<boolean>>;
  onSelectFromList: (ref: ElementRef) => void;
  canUndo: boolean;
  onUndo: () => void;
  canRedo: boolean;
  onRedo: () => void;
  onRunOrCancelSimulation: () => void;
  simulationWorker: ReturnType<typeof useSimulationWorker>;
  resultsDashboard: ReturnType<typeof useResultsDashboard>;
  resultsAnimationOpen: boolean;
  onToggleResultsAnimation: () => void;
  theme: Theme;
  onToggleTheme: () => void;
  language: Language;
  onChangeLanguage: (language: Language) => void;
}) {
  return (
    <header className="topbar">
      <Logo size={26} variant="chrome" />
      {/* The project's own NAME (chosen when it was opened, Landing.tsx) —
          never the underlying .inp's filename, which the user never sees
          again once a project is open. */}
      <span className="topbar-title" title={loaded.name}>
        {loaded.name}
      </span>
      <div className="toolbar">
        <ProjectMenu
          projectName={loaded.name}
          open={dialogs.open.projectMenu}
          onToggle={() => dialogs.toggleExclusive("projectMenu", TOOLBAR_MENU_GROUP)}
          onClose={() => dialogs.close("projectMenu")}
          onRename={onRenameProject}
          onDuplicate={onDuplicateProject}
          onExport={onExportInp}
          onDelete={onDeleteProject}
        />
        <IconButton
          className="topbar-icon-button"
          icon={<CloseProjectIcon />}
          label={t("toolbar.closeProject")}
          onClick={onCloseProject}
        />
      </div>
      <div className="topbar-spacer" />
      <div className="toolbar toolbar-separated">
        {mapMode === "plane" && (
          <IconButton
            className="topbar-icon-button"
            icon={<BackgroundImageIcon />}
            label={t("projectMenu.backgroundImage")}
            onClick={() => dialogs.openDialog("backgroundImage")}
          />
        )}
        {mapMode === "plane" && !loaded.crs && (
          <IconButton
            className="topbar-icon-button"
            icon={<GeoreferenceIcon />}
            label={t("toolbar.georeference")}
            onClick={() => dialogs.openDialog("georeference")}
          />
        )}
        {loaded.crs && (
          <IconButton
            className="topbar-icon-button"
            icon={mapMode === "plane" ? <GlobeIcon /> : <LayoutGridIcon />}
            label={mapMode === "plane" ? t("toolbar.toMapView") : t("toolbar.toPlaneView")}
            onClick={() => setMapMode(mapMode === "plane" ? "geo" : "plane")}
          />
        )}
        <MapSettingsMenu
          showOverview={showOverview}
          onToggleOverview={() => setShowOverview((show) => !show)}
          showLinkDirection={showLinkDirection}
          onToggleShowLinkDirection={() => setShowLinkDirection((show) => !show)}
          open={dialogs.open.mapSettingsMenu}
          onToggle={() => dialogs.toggle("mapSettingsMenu")}
          onClose={() => dialogs.close("mapSettingsMenu")}
        />
      </div>
      <div className="toolbar toolbar-separated">
        <IconButton
          className={
            sidebarVisible ? "topbar-icon-button toolbar-button-active" : "topbar-icon-button"
          }
          icon={<ListIcon />}
          label={t("toolbar.toggleElementList")}
          onClick={() => setSidebarVisible((visible) => !visible)}
        />
        <ElementSearch
          project={project}
          open={dialogs.open.search}
          onToggle={() => dialogs.toggle("search")}
          onClose={() => dialogs.close("search")}
          onSelect={onSelectFromList}
        />
      </div>
      <div className="toolbar toolbar-separated">
        <IconButton
          className="topbar-icon-button"
          icon={<UndoIcon />}
          label={t("toolbar.undo")}
          onClick={onUndo}
          disabled={!canUndo}
        />
        <IconButton
          className="topbar-icon-button"
          icon={<RedoIcon />}
          label={t("toolbar.redo")}
          onClick={onRedo}
          disabled={!canRedo}
        />
      </div>
      <div className="toolbar toolbar-separated">
        <IconButton
          className="topbar-icon-button"
          icon={<OptionsIcon />}
          label={t("projectMenu.simulationOptions")}
          onClick={() => dialogs.openDialog("options")}
        />
        <IconButton
          className="topbar-icon-button button-primary"
          icon={simulationWorker.simulating ? <StopIcon /> : <PlayIcon />}
          label={
            simulationWorker.simulating ? t("toolbar.cancelSimulation") : t("toolbar.runSimulation")
          }
          onClick={onRunOrCancelSimulation}
          // Every real edit (mutateRaw/undo/redo) already clears
          // `simulationWorker.results` immediately — so it being
          // non-null here means nothing has changed since the run that
          // produced it, and re-running would just burn a real
          // network's own simulation time (seconds to minutes) to
          // recompute the exact same numbers. Reported live.
          disabled={!simulationWorker.simulating && !!simulationWorker.results}
        />
        <IconButton
          className={
            resultsDashboard.open
              ? "topbar-icon-button toolbar-button-active"
              : "topbar-icon-button"
          }
          icon={<ResultsDashboardIcon />}
          label={t("toolbar.resultsDashboard")}
          onClick={() => resultsDashboard.toggle()}
          disabled={!simulationWorker.results}
        />
        <IconButton
          className="topbar-icon-button"
          icon={<SummaryIcon />}
          label={t("toolbar.simulationSummary")}
          onClick={() => dialogs.openDialog("simulationSummary")}
          disabled={!simulationWorker.results}
        />
        <IconButton
          className="topbar-icon-button"
          icon={<StatisticsIcon />}
          label={t("toolbar.statisticsReport")}
          onClick={() => dialogs.openDialog("statisticsReport")}
          disabled={!simulationWorker.results}
        />
        <IconButton
          className={
            resultsAnimationOpen ? "topbar-icon-button toolbar-button-active" : "topbar-icon-button"
          }
          icon={<ResultsAnimationIcon />}
          label={t("toolbar.resultsAnimation")}
          onClick={onToggleResultsAnimation}
          disabled={!simulationWorker.results}
        />
      </div>
      <div className="toolbar toolbar-separated">
        <SettingsMenu
          theme={theme}
          onToggleTheme={onToggleTheme}
          language={language}
          onChangeLanguage={onChangeLanguage}
          open={dialogs.open.settingsMenu}
          onToggle={() => dialogs.toggle("settingsMenu")}
          onClose={() => dialogs.close("settingsMenu")}
        />
      </div>
    </header>
  );
}
